import { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import apiClient from '../../services/api';

interface InvitationCode {
  id: number;
  code: string;
  status: string;
  use_count: number;
  max_uses: number | null;
  created_at: string;
}

interface InvitationStats {
  total_invited: number;
  active_invited: number;
  invitation_code: string;
}

interface InvitedUser {
  id: number;
  email: string;
  status: string;
  balance: number;
  registered_at: string;
}

export function InvitationPage() {
  const [invitationCode, setInvitationCode] = useState<InvitationCode | null>(null);
  const [invitationStats, setInvitationStats] = useState<InvitationStats | null>(null);
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCopied, setShowCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchInvitationCode();
    fetchInvitationStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'invited-users') {
      fetchInvitedUsers();
    }
  }, [activeTab, page, pageSize]);

  const fetchInvitationCode = async () => {
    try {
      const response = await apiClient.get('/invitations/my-code');
      setInvitationCode(response.data);
    } catch (error) {
      console.error('Failed to fetch invitation code:', error);
    }
  };

  const fetchInvitationStats = async () => {
    try {
      const response = await apiClient.get('/invitations/stats');
      setInvitationStats(response.data);
    } catch (error) {
      console.error('Failed to fetch invitation stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitedUsers = async () => {
    try {
      const response = await apiClient.get('/invitations/invited-users', {
        params: { page, page_size: pageSize }
      });
      setInvitedUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch invited users:', error);
    }
  };

  const copyInvitationCode = async () => {
    if (!invitationCode) return;
    
    try {
      const shareUrl = `${window.location.origin}/register?code=${invitationCode.code}`;
      await navigator.clipboard.writeText(shareUrl);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy invitation code:', error);
      alert('复制失败，请手动复制');
    }
  };

  const shareInvitationCode = async () => {
    if (!invitationCode) return;
    
    const shareUrl = `${window.location.origin}/register?code=${invitationCode.code}`;
    const shareText = '注册AI API Token平台，享受优质的AI服务！';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: '邀请你加入AI API Token平台',
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        console.error('Failed to share invitation code:', error);
        copyInvitationCode();
      }
    } else {
      copyInvitationCode();
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      disabled: 'bg-gray-100 text-gray-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      deleted: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      active: '活跃',
      disabled: '禁用',
      suspended: '已暂停',
      deleted: '已删除',
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">我的邀请码</h3>
            {invitationCode && (
              <div className="flex items-center space-x-4">
                <div className="bg-gray-100 px-4 py-2 rounded-lg font-mono text-lg">
                  {invitationCode.code}
                </div>
                <Button onClick={copyInvitationCode}>
                  {showCopied ? '已复制!' : '复制链接'}
                </Button>
                <Button onClick={shareInvitationCode} variant="secondary">
                  分享
                </Button>
              </div>
            )}
          </div>
          {invitationCode && (
            <div className="mt-4 md:mt-0">
              <p className="text-sm text-gray-500">状态: {getStatusBadge(invitationCode.status)}</p>
              <p className="text-sm text-gray-500 mt-1">使用次数: {invitationCode.use_count} / {invitationCode.max_uses || '无限制'}</p>
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">邀请统计</h3>
          {invitationStats ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">总邀请人数</p>
                <p className="text-3xl font-bold text-blue-600">{invitationStats.total_invited}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">活跃邀请人数</p>
                <p className="text-3xl font-bold text-green-600">{invitationStats.active_invited}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">邀请成功率</p>
                <p className="text-3xl font-bold text-purple-600">
                  {invitationStats.total_invited > 0 
                    ? `${Math.round((invitationStats.active_invited / invitationStats.total_invited) * 100)}%` 
                    : '0%'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">暂无数据</p>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">如何邀请</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
              <div>
                <p className="text-sm font-medium text-gray-900">复制邀请链接</p>
                <p className="text-sm text-gray-500">点击上方的"复制链接"按钮</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
              <div>
                <p className="text-sm font-medium text-gray-900">分享给朋友</p>
                <p className="text-sm text-gray-500">通过微信、邮件等方式分享链接</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
              <div>
                <p className="text-sm font-medium text-gray-900">朋友注册</p>
                <p className="text-sm text-gray-500">朋友通过链接注册后即可看到邀请记录</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderInvitedUsers = () => (
    <Card>
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">我的邀请用户</h3>
        <Button onClick={fetchInvitedUsers}>刷新</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ID</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">邮箱</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">状态</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">余额</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">注册时间</th>
            </tr>
          </thead>
          <tbody>
            {invitedUsers.map((user) => (
              <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm">{user.id}</td>
                <td className="py-3 px-4 text-sm">{user.email}</td>
                <td className="py-3 px-4">
                  {getStatusBadge(user.status)}
                </td>
                <td className="py-3 px-4 text-sm">${user.balance?.toFixed(2) || '0.00'}</td>
                <td className="py-3 px-4 text-sm">{user.registered_at}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-6 border-t border-gray-200 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          共 {total} 条记录
        </div>
        <div className="flex space-x-2">
          <Button
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <Button
            size="sm"
            disabled={page * pageSize >= total}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">邀请管理</h1>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('overview');
              setPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            概览
          </button>
          <button
            onClick={() => {
              setActiveTab('invited-users');
              setPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'invited-users' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            我的邀请用户
          </button>
        </nav>
      </div>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'invited-users' && renderInvitedUsers()}
    </div>
  );
}
